import { useEditField } from '@atlassian/jira-issue-field-base';
import { CATEGORY_TYPE } from '@atlassian/jira-platform-field-config';
import { useGenericClipboardHandler } from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	PasteHandler,
	PasteMetadata,
} from '../../../types';
import { createClipboardHelperComponent } from '../../utils';
import { categorySupportedTypes } from '../supported-field-types';
import type { TransformedCategoryOption, CategoryFieldValueToSave } from '../types';

const isTransformedCategory = (fieldValue: unknown): fieldValue is TransformedCategoryOption =>
	fieldValue != null &&
	typeof fieldValue === 'object' &&
	'value' in fieldValue &&
	'optionId' in fieldValue;

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): CategoryFieldValueToSave | null => {
	if (!clipboardItem || !metadata?.fieldValue || !metadata?.fieldType) {
		return null;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	if (!(categorySupportedTypes as string[]).includes(metadata.fieldType)) {
		return null;
	}

	if (!isTransformedCategory(metadata.fieldValue)) {
		return null;
	}

	return {
		id: metadata.fieldValue.optionId,
		value: metadata.fieldValue.value,
		color: metadata.fieldValue.color,
	};
};

const useCategoryClipboardHandler: PasteHandler = (
	issue: { key: string },
	field: ListIssueFieldTransformed,
) => {
	const [, { saveValue }] = useEditField<CategoryFieldValueToSave | null, null>({
		fieldKey: field.fieldId,
		issueKey: issue.key,
		fieldType: CATEGORY_TYPE,
		initialValue:
			field.value && isTransformedCategory(field.value)
				? {
						id: field.value.optionId,
						value: field.value.value,
						color: field.value?.color,
					}
				: null,
	});

	return useGenericClipboardHandler(field, getPastedValue, saveValue);
};

const CategoryClipboardHelper = createClipboardHelperComponent(useCategoryClipboardHandler);

export const categoryClipboardHandler = {
	[CATEGORY_TYPE]: CategoryClipboardHelper,
};
